<script>
  /**
   * SelectionControls — a toolbar's delete-only selection cluster, used by the
   * asset libraries and the targeting account tables.
   *
   * Off: a single Select toggle. On: the selected count, Select All, a delete
   * button (count and delete hidden at zero), and Cancel. Select All lives here as
   * well as in the table header so card view gets it too; it covers `rows`, the
   * visible set, and flips to Deselect All once every row is selected.
   *
   * @example
   * <SelectionControls
   *   bind:selecting
   *   {selection}
   *   rows={filtered}
   *   id="images"
   *   onDelete={() => (showBulkDelete = true)}
   * />
   */

  import { format } from 'svelte-i18n';
  import { Button, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  /**
   * Whether selection mode is active. Use `bind:selecting`.
   * @type {boolean}
   */
  export let selecting = false;

  /**
   * A selection store from `createSelection()`.
   * @type {{ subscribe: Function, toggle: Function, selectAll: Function, clear: Function, prune: Function }}
   */
  export let selection;

  /**
   * The currently visible (filtered) rows. Select-all and the count are scoped
   * to these, never to the unfiltered library.
   * @type {Object[]}
   */
  export let rows = [];

  /**
   * Called when the delete button is clicked.
   * @type {() => void}
   */
  export let onDelete = () => {};

  /**
   * Prefix for tooltip target ids, so several instances can coexist.
   * @type {string}
   */
  export let id = 'assets';

  const enable = () => {
    selecting = true;
  };

  const cancel = () => {
    selecting = false;
    selection.clear();
  };

  $: count = $selection.size;
  // `>=` rather than `===` so a stale row list can't strand the button on "Select All".
  $: allSelected = rows.length > 0 && count >= rows.length;
</script>

<div class="selection d-flex align-items-center gap-2">
  {#if selecting}
    {#if count}
      <small class="text-muted text-nowrap" data-testid="selection-count">
        {$format('label.BULK_DELETE.SELECTED_COUNT', { values: { checked: count, total: rows.length } })}
      </small>
    {/if}

    <Button
      id="{id}-select-all-btn"
      color="link"
      size="sm"
      class="px-1"
      on:click={() => (allSelected ? selection.clear() : selection.selectAll(rows))}
    >
      {$format(allSelected ? 'label.DESELECT_ALL' : 'label.SELECT_ALL')}
    </Button>

    <!-- `count` is guarded twice so Select All can sit between the count and Delete. -->
    {#if count}
      <Button id="{id}-bulk-delete-btn" color="danger" outline on:click={onDelete}>
        <Icon class="d-inline d-md-none" name="trash-2" />
        <span class="d-none d-md-inline">{$format('label.DELETE')}</span>
      </Button>
      <Tooltip target="{id}-bulk-delete-btn" placement="top">
        {$format('label.BULK_DELETE.DELETE_SELECTED')}
      </Tooltip>
    {/if}

    <Button style="padding: calc(var(--padding-base) - 3px) calc(var(--padding-base) + 2px)" on:click={cancel}>
      <Icon class="d-inline d-md-none" name="x" />
      <span class="d-none d-md-inline">{$format('label.CANCEL')}</span>
    </Button>
  {:else}
    <Button id="{id}-select-btn" on:click={enable}>
      <span>{$format('label.SELECT')}</span>
    </Button>
    <Tooltip target="{id}-select-btn" placement="top">{$format('label.SELECT')}</Tooltip>
  {/if}
</div>

<style lang="scss">
  :global(.selection .btn) {
    padding: calc(var(--padding-base) - 3px) calc(var(--padding-base) + 2px);
  }
</style>
